import type { ReactNode } from "react";
import type { TooltipContentProps } from "recharts";

import styles from "./chart-tooltip.module.css";

// `Payload` isn't re-exported from recharts' root; derive the entry type from
// the exported `TooltipContentProps`'s own `payload` field instead.
export type ChartTooltipPayload = NonNullable<TooltipContentProps<number, string>["payload"]>[number];

export type ChartTooltipProps = TooltipContentProps<number, string> & {
  /** Formats the tooltip's label (usually the x-axis value) for display. */
  labelFormatter?: (label: unknown) => ReactNode;
  /**
   * Formats one series' value for display. Receives the full payload entry
   * (so a caller can read sibling fields off `entry.payload`, e.g. to append
   * a raw count next to a formatted percentage).
   */
  valueFormatter?: (value: number | undefined, entry: ChartTooltipPayload) => ReactNode;
  /** Optional trailing line below the series rows, e.g. a total. */
  footer?: (payload: ReadonlyArray<ChartTooltipPayload>) => ReactNode;
};

/**
 * Presentational Recharts tooltip content, themed once with design tokens
 * (`--nzr-card`/`--ink`/`--nzr-border`/`--elevation-tooltip`) so every chart
 * gets a legible tooltip in both themes instead of styling its own. Render
 * only — no data fetching, no chart-specific logic; pass formatting and any
 * extra content in as props:
 *
 * ```tsx
 * <Tooltip content={(props) => (
 *   <ChartTooltip {...props} labelFormatter={formatDateTick} valueFormatter={(value) => formatPercent(value ?? 0)} />
 * )} />
 * ```
 */
export function ChartTooltip({ active, payload, label, labelFormatter, valueFormatter, footer }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tooltip}>
      <p className={styles.tooltipLabel}>{labelFormatter ? labelFormatter(label) : (label as ReactNode)}</p>
      {payload.map((entry) => (
        <p key={entry.name} style={{ color: entry.color }}>
          {entry.name}: {valueFormatter ? valueFormatter(entry.value, entry) : entry.value}
        </p>
      ))}
      {footer && <p className={styles.tooltipFooter}>{footer(payload)}</p>}
    </div>
  );
}
